// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { type AskPunttRememberedContext } from "@mg/schemas/src/christo/askPunttBriefing";
import { Button, Text, TextArea } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type RememberedContextProps = {
  items: AskPunttRememberedContext[];
  onCorrect: (id: string, text: string) => void | Promise<unknown>;
  savingId?: string | null;
};

export function RememberedContext({
  items,
  onCorrect,
  savingId = null,
}: RememberedContextProps) {
  const { t } = useTranslation("ui");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  if (items.length === 0) return null;

  return (
    <section
      data-testid="ask-v2-remembered"
      className="flex w-full max-w-2xl flex-col gap-2"
    >
      <Text as="p" size="2" weight="medium">
        {t("askV2.home.remembered")}
      </Text>
      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li
            key={item.id}
            data-testid="ask-v2-memory-item"
            className="rounded-xl bg-puntt-neutral-gray-2 px-3 py-2"
          >
            {editingId === item.id ? (
              <div className="flex flex-col gap-2">
                <TextArea
                  data-testid="ask-v2-memory-draft"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    size="1"
                    data-testid="ask-v2-memory-save"
                    disabled={!draft.trim() || savingId === item.id}
                    onClick={() => {
                      void Promise.resolve(
                        onCorrect(item.id, draft.trim()),
                      ).then(
                        () => {
                          setEditingId(null);
                        },
                        () => {
                          // why: a failed patch must keep the draft, not discard it.
                        },
                      );
                    }}
                  >
                    {t("askV2.transport.saveEdit")}
                  </Button>
                  <Button
                    size="1"
                    variant="ghost"
                    data-testid="ask-v2-memory-cancel"
                    onClick={() => setEditingId(null)}
                  >
                    {t("askV2.transport.cancelEdit")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <Text as="p" size="2">
                  {item.text}
                </Text>
                {item.correctable ? (
                  <Button
                    size="1"
                    variant="ghost"
                    data-testid="ask-v2-memory-edit"
                    onClick={() => {
                      setEditingId(item.id);
                      setDraft(item.text);
                    }}
                  >
                    {t("askV2.transport.edit")}
                  </Button>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
